import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, ToggleButtonGroup, ToggleButton,
  Slider, TextField, Select, MenuItem, FormControl, Button, Chip,
  Divider, CircularProgress, Alert,
} from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import ChatChartPanel from './ChatChartPanel';
import { workforceApi } from '../services/hrmsApi';

const SCENARIOS = [
  { value: 'hire',             label: 'Hire' },
  { value: 'attrition',        label: 'Attrition' },
  { value: 'raise',            label: 'Salary Raise' },
  { value: 'leave_encashment', label: 'Leave Encashment' },
];

const DEPARTMENTS = ['All Departments', 'Sales', 'Finance', 'IT', 'Operations', 'HR'];

// Stub responses used while the backend endpoint is not yet delivered
const STUB = {
  hire: {
    scenarioLabel: 'Hiring 20 in Sales over 6 months (stub preview)',
    chartType: 'line',
    chartData: JSON.stringify([
      { month_label: 'Jul 2026', actual: 1050, projected: null },
      { month_label: 'Aug 2026', actual: 1055, projected: null },
      { month_label: 'Sep 2026', actual: 1058, projected: null },
      { month_label: 'Oct 2026', actual: null, projected: 1063 },
      { month_label: 'Nov 2026', actual: null, projected: 1068 },
      { month_label: 'Dec 2026', actual: null, projected: 1070 },
    ]),
    yAxisLabel: 'Employee Count',
    summaryText: 'Projected headcount reaches 1,070 by Dec 2026, adding approximately RM 84,000/year in additional payroll costs.',
    kpiDelta: { headcountDelta: 20, payrollDeltaRM: 84000, attritionDelta: null, leaveLiabilityDelta: null },
  },
  attrition: {
    scenarioLabel: 'Attrition rises to 8% over 6 months (stub preview)',
    chartType: 'line',
    chartData: JSON.stringify([
      { month_label: 'Jul 2026', actual: 5.2, projected: null },
      { month_label: 'Aug 2026', actual: 5.8, projected: null },
      { month_label: 'Sep 2026', actual: 6.1, projected: null },
      { month_label: 'Oct 2026', actual: null, projected: 6.8 },
      { month_label: 'Nov 2026', actual: null, projected: 7.4 },
      { month_label: 'Dec 2026', actual: null, projected: 8.0 },
    ]),
    yAxisLabel: 'Attrition Rate (%)',
    summaryText: 'If attrition reaches 8%, an estimated 84 employees will exit over the next 6 months, placing significant pressure on the recruitment pipeline.',
    kpiDelta: { headcountDelta: -84, payrollDeltaRM: null, attritionDelta: 2.8, leaveLiabilityDelta: null },
  },
  raise: {
    scenarioLabel: '5% salary raise across all staff (stub preview)',
    chartType: 'line',
    chartData: JSON.stringify([
      { month_label: 'Jul 2026', actual: 4200000, projected: null },
      { month_label: 'Aug 2026', actual: 4250000, projected: null },
      { month_label: 'Sep 2026', actual: 4230000, projected: null },
      { month_label: 'Oct 2026', actual: null, projected: 4441500 },
      { month_label: 'Nov 2026', actual: null, projected: 4462500 },
      { month_label: 'Dec 2026', actual: null, projected: 4441500 },
    ]),
    yAxisLabel: 'Monthly Payroll (RM)',
    summaryText: 'A 5% raise adds approximately RM 210,000/month (RM 2.52M/year) to payroll, bringing annual budget utilisation to ~92%.',
    kpiDelta: { headcountDelta: null, payrollDeltaRM: 210000, attritionDelta: null, leaveLiabilityDelta: null },
  },
  leave_encashment: {
    scenarioLabel: '50% leave encashment at year-end (stub preview)',
    chartType: 'line',
    chartData: JSON.stringify([
      { month_label: 'Jul 2026', actual: 287000, projected: null },
      { month_label: 'Aug 2026', actual: 295000, projected: null },
      { month_label: 'Sep 2026', actual: 302000, projected: null },
      { month_label: 'Oct 2026', actual: null, projected: 158500 },
      { month_label: 'Nov 2026', actual: null, projected: 155000 },
      { month_label: 'Dec 2026', actual: null, projected: 158500 },
    ]),
    yAxisLabel: 'Leave Liability (RM)',
    summaryText: '50% leave encashment reduces year-end liability by ~RM 143,500, easing payroll accruals and reducing burnout risk from unused leave.',
    kpiDelta: { headcountDelta: null, payrollDeltaRM: null, attritionDelta: null, leaveLiabilityDelta: -143500 },
  },
};

const sliderMarks = (min, mid, max, suffix = '%') => [
  { value: min, label: `${min}${suffix}` },
  { value: mid, label: `${mid}${suffix}` },
  { value: max, label: `${max}${suffix}` },
];

export default function SimulationPanel() {
  const [scenario,        setScenario]        = useState('hire');
  const [hireCount,       setHireCount]       = useState(20);
  const [department,      setDepartment]      = useState('Sales');
  const [targetAttrition, setTargetAttrition] = useState(8);
  const [raisePct,        setRaisePct]        = useState(5);
  const [encashmentPct,   setEncashmentPct]   = useState(50);
  const [horizonMonths,   setHorizonMonths]   = useState(6);
  const [result,          setResult]          = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [isStub,          setIsStub]          = useState(false);

  const buildPayload = () => ({
    scenario,
    params: {
      ...(scenario === 'hire'             && { hireCount, department: department === 'All Departments' ? null : department }),
      ...(scenario === 'attrition'        && { targetAttritionPct: targetAttrition }),
      ...(scenario === 'raise'            && { raisePct }),
      ...(scenario === 'leave_encashment' && { encashmentPct }),
    },
    horizonMonths,
  });

  const handleRun = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setIsStub(false);
    try {
      const res = await workforceApi.simulate(buildPayload());
      setResult(res);
    } catch (e) {
      // Fall back to stub when endpoint is not yet delivered
      if (!e.response || e.response.status === 404 || e.response.status === 501) {
        setResult(STUB[scenario]);
        setIsStub(true);
      } else {
        setError(e.response?.data?.message || 'Simulation failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleScenarioChange = (_, v) => {
    if (!v) return;
    setScenario(v);
    setResult(null);
    setError(null);
  };

  const deltaSign = (v) => (v >= 0 ? '+' : '');

  const kpiChips = result ? [
    result.kpiDelta?.headcountDelta != null && {
      label: `Headcount ${deltaSign(result.kpiDelta.headcountDelta)}${result.kpiDelta.headcountDelta}`,
      color: result.kpiDelta.headcountDelta >= 0 ? 'success' : 'error',
    },
    result.kpiDelta?.payrollDeltaRM != null && {
      label: `Payroll ${deltaSign(result.kpiDelta.payrollDeltaRM)}RM ${Math.abs(result.kpiDelta.payrollDeltaRM).toLocaleString()}/yr`,
      color: result.kpiDelta.payrollDeltaRM > 0 ? 'warning' : 'success',
    },
    result.kpiDelta?.attritionDelta != null && {
      label: `Attrition ${deltaSign(result.kpiDelta.attritionDelta)}${result.kpiDelta.attritionDelta}%`,
      color: result.kpiDelta.attritionDelta > 0 ? 'error' : 'success',
    },
    result.kpiDelta?.leaveLiabilityDelta != null && {
      label: `Leave Liability ${deltaSign(result.kpiDelta.leaveLiabilityDelta)}RM ${Math.abs(result.kpiDelta.leaveLiabilityDelta).toLocaleString()}`,
      color: result.kpiDelta.leaveLiabilityDelta < 0 ? 'success' : 'warning',
    },
  ].filter(Boolean) : [];

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Workforce Scenario Simulator
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={2.5}>
          Model the projected impact of HR decisions before they are made
        </Typography>

        {/* Scenario selector */}
        <ToggleButtonGroup
          value={scenario} exclusive onChange={handleScenarioChange}
          size="small" sx={{ mb: 3, flexWrap: 'wrap', gap: 0.5 }}
        >
          {SCENARIOS.map((s) => (
            <ToggleButton key={s.value} value={s.value}
              sx={{ textTransform: 'none', px: 2.5, fontWeight: 600 }}>
              {s.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Parameter controls */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 3, alignItems: 'flex-start' }}>

          {scenario === 'hire' && (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Headcount to Add
                </Typography>
                <TextField
                  type="number" size="small" value={hireCount}
                  onChange={(e) => setHireCount(Math.max(1, Math.min(500, Number(e.target.value))))}
                  inputProps={{ min: 1, max: 500 }} sx={{ width: 130 }}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Department
                </Typography>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select value={department} onChange={(e) => setDepartment(e.target.value)}>
                    {DEPARTMENTS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </>
          )}

          {scenario === 'attrition' && (
            <Box sx={{ minWidth: 280 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Target Attrition Rate: <strong>{targetAttrition}%</strong>
              </Typography>
              <Slider value={targetAttrition} onChange={(_, v) => setTargetAttrition(v)}
                min={1} max={20} step={0.5}
                marks={sliderMarks(1, 10, 20)}
                valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}%`}
              />
            </Box>
          )}

          {scenario === 'raise' && (
            <Box sx={{ minWidth: 280 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Salary Raise (all staff): <strong>{raisePct}%</strong>
              </Typography>
              <Slider value={raisePct} onChange={(_, v) => setRaisePct(v)}
                min={1} max={20} step={0.5}
                marks={sliderMarks(1, 10, 20)}
                valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}%`}
              />
            </Box>
          )}

          {scenario === 'leave_encashment' && (
            <Box sx={{ minWidth: 280 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Encash Accrued Leave Balance: <strong>{encashmentPct}%</strong>
              </Typography>
              <Slider value={encashmentPct} onChange={(_, v) => setEncashmentPct(v)}
                min={10} max={100} step={5}
                marks={sliderMarks(10, 50, 100)}
                valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}%`}
              />
            </Box>
          )}

          {scenario !== 'leave_encashment' && (
            <Box sx={{ minWidth: 240 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Projection Horizon: <strong>{horizonMonths} months</strong>
              </Typography>
              <Slider value={horizonMonths} onChange={(_, v) => setHorizonMonths(v)}
                min={1} max={12} step={1}
                marks={[{value:3,label:'3m'},{value:6,label:'6m'},{value:12,label:'12m'}]}
                valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}m`}
              />
            </Box>
          )}
        </Box>

        <Button
          variant="contained" onClick={handleRun} disabled={loading}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PlayArrow />}
          sx={{ mb: 2, textTransform: 'none', fontWeight: 700, bgcolor: '#1565C0', '&:hover': { bgcolor: '#0D47A1' } }}
        >
          {loading ? 'Running…' : 'Run Simulation'}
        </Button>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {result && (
          <>
            <Divider sx={{ my: 2 }} />

            {isStub && (
              <Alert severity="info" sx={{ mb: 2, py: 0.5, fontSize: '0.75rem' }}>
                Stub preview — backend endpoint not yet delivered. Data is illustrative only.
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={1.5}>
              {result.scenarioLabel}
            </Typography>

            {kpiChips.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {kpiChips.map((chip) => (
                  <Chip key={chip.label} label={chip.label} color={chip.color}
                    size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                ))}
              </Box>
            )}

            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 2, py: 1.5, mb: 2 }}>
              <Typography variant="body2" color="text.primary">{result.summaryText}</Typography>
            </Box>

            <ChatChartPanel
              chartType={result.chartType}
              chartData={result.chartData}
              yAxisLabel={result.yAxisLabel}
              height={240}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
